import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  ThreadSubscriptionFormAction,
  updateIssueSubscriptionMutation,
  updateIssueSubscriptionMutation$data,
  UpdateSubscriptionInput,
} from './__generated__/updateIssueSubscriptionMutation.graphql'

function toObjectPropertiesPatch(data: updateIssueSubscriptionMutation$data): ObjectPropertiesPatch | undefined {
  const issue = data.updateSubscription?.subscribable
  const issueId = issue?.id
  if (!issue || !issueId) return undefined
  return {
    id: issueId,
    values: {
      viewerThreadSubscriptionFormAction: issue.viewerThreadSubscriptionFormAction,
      viewerCustomSubscriptionEvents: issue.viewerCustomSubscriptionEvents,
    },
  }
}

export function commitUpdateIssueSubscriptionMutation({
  environment,
  input,
  onCompleted,
  onError,
}: {
  environment: Environment
  input: UpdateSubscriptionInput
  onCompleted?: () => void
  onError?: (error: Error) => void
}) {
  const viewerThreadSubscriptionFormAction: ThreadSubscriptionFormAction =
    input.state === 'SUBSCRIBED' || input.state === 'CUSTOM' ? 'UNSUBSCRIBE' : 'SUBSCRIBE'

  const optimisticResponse = {
    updateSubscription: {
      subscribable: {
        __typename: 'Issue',
        id: input.subscribableId,
        viewerThreadSubscriptionFormAction,
        viewerCustomSubscriptionEvents: input.events,
      },
    },
  }

  const optimisticPatch = toObjectPropertiesPatch(optimisticResponse)
  const optimisticEvent =
    optimisticPatch &&
    new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'optimistic', data: optimisticPatch})
  const mutationId = optimisticEvent?.detail.mutationId ?? createMutationId()

  const disposable = commitMutation<updateIssueSubscriptionMutation>(environment, {
    // Important: the name of this mutation is being used in app/platform/authorization/permission_check.rb (def emu_ownership_enforceable)
    // to decide whether EMU can subscribe to an issue in a public repo. If changing it, please change it there as well.
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation updateIssueSubscriptionMutation($input: UpdateSubscriptionInput!) @raw_response_type {
        updateSubscription(input: $input) {
          subscribable {
            ... on Issue {
              id
              viewerThreadSubscriptionFormAction
              viewerCustomSubscriptionEvents
            }
          }
        }
      }
    `,
    variables: {
      input,
    },
    optimisticResponse,
    onCompleted: response => {
      const patch = toObjectPropertiesPatch(response)
      if (patch) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
      }
      onCompleted?.()
    },
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      onError?.(error)
    },
  })

  if (optimisticEvent) {
    dispatchMutationLifecycleEvent(optimisticEvent)
  }

  return disposable
}
